import { Component, EventEmitter, Input, Output, OnInit, effect, ViewChild, inject } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { LocationSelectorComponentState } from "./State/LocationSelectorComponentState";
import { LocationSelectorComponentEffects } from "./State/LocationSelectorComponentEffects";
import { LocationOption, LocationAddress } from "./Type/LocationSelectorTypes";
import { LocationNode } from "./Dto/LocationData";
import { AutocompleteComponent } from "../Autocomplete/AutocompleteComponent";

/**
 * Cascading location selector component for Indonesian administrative divisions.
 *
 * Provides a hierarchical selection experience for Province → District → Subdistrict → Village,
 * with autocomplete functionality at each level. Follows the state/effects pattern for
 * state management and side effects.
 *
 * @example
 * ```html
 * <app-location-selector [value]="address" (valueChange)="onLocationChange($event)" />
 * ```
 *
 * @remarks
 * Location data is loaded from `Assets/Libs/Locations.json` using a nested tree structure
 * with code format: `Province.District.Subdistrict.Village` (e.g., "11.10.10.2001")
 */
@Component({
  selector: "app-location-selector",
  standalone: true,
  imports: [CommonModule, FormsModule, AutocompleteComponent],
  templateUrl: "./LocationSelectorComponent.html",
  styleUrl: "./LocationSelectorComponent.scss",
  providers: [LocationSelectorComponentState, LocationSelectorComponentEffects],
})
export class LocationSelectorComponent implements OnInit {
  protected readonly locationState = inject(LocationSelectorComponentState);
  private readonly locationEffects = inject(LocationSelectorComponentEffects);

  @ViewChild("provinceAutocomplete") provinceAutocomplete!: AutocompleteComponent;
  @ViewChild("districtAutocomplete") districtAutocomplete!: AutocompleteComponent;
  @ViewChild("subdistrictAutocomplete") subdistrictAutocomplete!: AutocompleteComponent;
  @ViewChild("villageAutocomplete") villageAutocomplete!: AutocompleteComponent;

  /**
   * Initial/pre-filled address value for two-way binding.
   *
   * When set, applies the address values to the component's selection state.
   * If location data hasn't loaded yet, the value is pending until data is available.
   *
   * @param val - The LocationAddress object containing province, district, subdistrict,
   * village, address line, and ZIP code information
   */
  @Input() set value(val: LocationAddress | undefined) {
    if (val) {
      this.addressLine = val.line || "";
      this.zipCode = val.zip || "";

      this.pendingValue = val;

      if (val.stateProvinceRegion && this.locationState.isLoaded()) {
        this.applyValue(val);
        this.pendingValue = null;
      }
    }
  }

  /**
   * Applies a LocationAddress value to the component's selection state.
   *
   * Resolves location names to their corresponding codes and cascades through
   * province → district → subdistrict → village hierarchy.
   *
   * @param val - The LocationAddress to apply
   */
  private applyValue(val: LocationAddress): void {
    const locations = this.locationState.locations();

    const provinceCode = this.resolveProvinceCode(locations, val.stateProvinceRegion);

    if (provinceCode) {
      this.locationEffects.selectProvince(provinceCode);
      this.selectedProvince = provinceCode;

      if (val.countyDistrict) {
        const districtCode = this.resolveDistrictCode(locations, provinceCode, val.countyDistrict);

        if (districtCode) {
          this.locationEffects.selectDistrict(districtCode);
          this.selectedDistrict = districtCode;

          if (val.subdistrictMunicipality) {
            const subdistrictCode = this.resolveSubdistrictCode(locations, districtCode, val.subdistrictMunicipality);

            if (subdistrictCode) {
              this.locationEffects.selectSubdistrict(subdistrictCode);
              this.selectedSubdistrict = subdistrictCode;

              if (val.communeVillage) {
                const villageCode = this.resolveVillageCode(locations, subdistrictCode, val.communeVillage);
                if (villageCode) {
                  this.selectedVillage = villageCode;
                }
              }
            }
          }
        }
      }
    }
  }

  /**
   * Resolves a province name to its code.
   *
   * @param locations - The location data tree
   * @param label - The province name to resolve (case-insensitive)
   * @returns The province code or null if not found
   */
  private resolveProvinceCode(locations: Record<string, LocationNode>, label: string): string | null {
    for (const [code, node] of Object.entries(locations)) {
      if (node.name?.toLowerCase() === label.toLowerCase()) {
        return code;
      }
    }
    return null;
  }

  /**
   * Resolves a district name to its full code within a province.
   *
   * @param locations - The location data tree
   * @param provinceCode - The parent province code
   * @param label - The district name to resolve (case-insensitive)
   * @returns The full district code (e.g., "11.01") or null if not found
   */
  private resolveDistrictCode(locations: Record<string, LocationNode>, provinceCode: string, label: string): string | null {
    const province = locations[provinceCode];
    if (!province) {
      return null;
    }

    for (const [code, node] of Object.entries(province)) {
      if (code === "name" || code === "level" || code === "code") continue;
      if (typeof node === "object" && node !== null && (node as LocationNode).name?.toLowerCase() === label.toLowerCase()) {
        const fullCode = `${provinceCode}.${code}`;
        return fullCode;
      }
    }
    return null;
  }

  /**
   * Resolves a subdistrict name to its full code within a district.
   *
   * @param locations - The location data tree
   * @param districtCode - The parent district code (format: "province.district")
   * @param label - The subdistrict name to resolve (case-insensitive)
   * @returns The full subdistrict code (e.g., "11.01.01") or null if not found
   */
  private resolveSubdistrictCode(locations: Record<string, LocationNode>, districtCode: string, label: string): string | null {
    const parts = districtCode.split(".");
    if (parts.length !== 2) {
      return null;
    }

    const province = locations[parts[0]];
    if (!province) return null;

    const district = province[parts[1]] as LocationNode | undefined;
    if (!district) {
      return null;
    }

    for (const [code, node] of Object.entries(district)) {
      if (code === "name" || code === "level" || code === "code") continue;
      if (typeof node === "object" && node !== null && (node as LocationNode).name?.toLowerCase() === label.toLowerCase()) {
        const fullCode = `${districtCode}.${code}`;
        return fullCode;
      }
    }
    return null;
  }

  /**
   * Resolves a village name to its full code within a subdistrict.
   *
   * @param locations - The location data tree
   * @param subdistrictCode - The parent subdistrict code (format: "province.district.subdistrict")
   * @param label - The village name to resolve (case-insensitive)
   * @returns The full village code (e.g., "11.01.01.0001") or null if not found
   */
  private resolveVillageCode(locations: Record<string, LocationNode>, subdistrictCode: string, label: string): string | null {
    const parts = subdistrictCode.split(".");
    if (parts.length !== 3) {
      return null;
    }

    const province = locations[parts[0]];
    if (!province) return null;

    const district = province[parts[1]] as LocationNode | undefined;
    if (!district) return null;

    const subdistrict = district[parts[2]] as LocationNode | undefined;
    if (!subdistrict) {
      return null;
    }

    for (const [code, node] of Object.entries(subdistrict)) {
      if (code === "name" || code === "level" || code === "code") continue;
      if (typeof node === "object" && node !== null && (node as LocationNode).name?.toLowerCase() === label.toLowerCase()) {
        const fullCode = `${subdistrictCode}.${code}`;
        return fullCode;
      }
    }
    return null;
  }

  /**
   * Event emitted when the address value changes.
   *
   * Emits a LocationAddress object containing the selected location hierarchy
   * and address details.
   */
  @Output() valueChange = new EventEmitter<LocationAddress>();

  /**
   * Event emitted when the address value changes (alias for valueChange).
   *
   * Emits a LocationAddress object containing the selected location hierarchy
   * and address details.
   */
  @Output() addressChange = new EventEmitter<LocationAddress>();

  /**
   * List of available provinces as selectable options.
   */
  provinces: LocationOption[] = [];

  /**
   * List of available districts (filtered by selected province) as selectable options.
   */
  districts: LocationOption[] = [];

  /**
   * List of available subdistricts (filtered by selected district) as selectable options.
   */
  subdistricts: LocationOption[] = [];

  /**
   * List of available villages (filtered by selected subdistrict) as selectable options.
   */
  villages: LocationOption[] = [];

  /**
   * Currently selected province code.
   */
  selectedProvince = "";

  /**
   * Currently selected district code.
   */
  selectedDistrict = "";

  /**
   * Currently selected subdistrict code.
   */
  selectedSubdistrict = "";

  /**
   * Currently selected village code.
   */
  selectedVillage = "";

  /**
   * Address line (street address) input value.
   */
  addressLine = "";

  /**
   * ZIP/postal code input value.
   */
  zipCode = "";

  /**
   * Pending address value waiting for location data to load.
   */
  private pendingValue: LocationAddress | null = null;

  constructor() {
    effect(() => {
      this.provinces = this.locationState.provinces();
    });

    effect(() => {
      this.districts = this.locationState.districts();
    });

    effect(() => {
      this.subdistricts = this.locationState.subdistricts();
    });

    effect(() => {
      this.villages = this.locationState.villages();
    });

    effect(() => {
      const isLoaded = this.locationState.isLoaded();
      if (isLoaded && this.pendingValue) {
        this.applyValue(this.pendingValue);
        this.pendingValue = null;
      }
    });
  }

  /**
   * Initializes the component by loading location data.
   *
   * Loads the Indonesian administrative divisions from the JSON data source
   * on component initialization.
   */
  ngOnInit(): void {
    this.locationEffects.loadLocations().catch((err) => {
      console.error("[LocationSelector] Failed to load locations:", err);
    });
  }

  /**
   * Handles province selection and cascades the change to child levels.
   *
   * Clears district, subdistrict, and village selections when a new province
   * is selected, then loads the districts for the selected province.
   *
   * @param value - The province code or value (ignores array emissions)
   */
  selectProvince(value: string | number | (string | number)[]): void {
    if (Array.isArray(value)) {
      return;
    }
    this.selectedProvince = String(value);
    this.selectedDistrict = "";
    if (this.districtAutocomplete) this.districtAutocomplete.clearSelection();
    if (this.subdistrictAutocomplete) this.subdistrictAutocomplete.clearSelection();
    if (this.villageAutocomplete) this.villageAutocomplete.clearSelection();
    this.locationEffects.selectProvince(String(value));
    this.emitValue();
  }

  /**
   * Handles district selection and cascades the change to child levels.
   *
   * Clears subdistrict and village selections when a new district is selected,
   * then loads the subdistricts for the selected district.
   *
   * @param value - The district code or value (ignores array emissions)
   */
  selectDistrict(value: string | number | (string | number)[]): void {
    if (Array.isArray(value)) {
      return;
    }
    this.selectedDistrict = String(value);
    if (this.subdistrictAutocomplete) this.subdistrictAutocomplete.clearSelection();
    if (this.villageAutocomplete) this.villageAutocomplete.clearSelection();
    this.locationEffects.selectDistrict(String(value));
    this.emitValue();
  }

  /**
   * Handles subdistrict selection and cascades the change to child levels.
   *
   * Clears village selection when a new subdistrict is selected,
   * then loads the villages for the selected subdistrict.
   *
   * @param value - The subdistrict code or value (ignores array emissions)
   */
  selectSubdistrict(value: string | number | (string | number)[]): void {
    if (Array.isArray(value)) {
      return;
    }
    this.selectedSubdistrict = String(value);
    this.selectedVillage = "";
    if (this.villageAutocomplete) this.villageAutocomplete.clearSelection();
    this.locationEffects.selectSubdistrict(String(value));
    this.emitValue();
  }

  /**
   * Handles village selection.
   *
   * Completes the location hierarchy selection and emits the updated address value.
   *
   * @param value - The village code or value (ignores array emissions)
   */
  selectVillage(value: string | number | (string | number)[]): void {
    if (Array.isArray(value)) {
      return;
    }
    this.selectedVillage = String(value);
    this.locationEffects.selectVillage(String(value));
    this.emitValue();
  }

  /**
   * Handles changes to the address line input.
   *
   * @param value - The new address line value
   */
  onAddressLineChange(value: string): void {
    this.addressLine = value;
    this.emitValue();
  }

  /**
   * Handles changes to the ZIP code input.
   *
   * @param value - The new ZIP code value
   */
  onZipCodeChange(value: string): void {
    this.zipCode = value;
    this.emitValue();
  }

  /**
   * Emits the current address value as a LocationAddress object.
   *
   * Constructs a LocationAddress from the current selection state,
   * resolving codes to their display labels, and emits via valueChange
   * and addressChange events.
   */
  private emitValue(): void {
    const provinceLabel = this.getOptionLabel(this.provinces, this.provinceAutocomplete?.searchTerm || this.selectedProvince);
    const districtLabel = this.getOptionLabel(this.districts, this.districtAutocomplete?.searchTerm || this.selectedDistrict);
    const subdistrictLabel = this.getOptionLabel(
      this.subdistricts,
      this.subdistrictAutocomplete?.searchTerm || this.selectedSubdistrict,
    );
    const villageLabel = this.getOptionLabel(this.villages, this.villageAutocomplete?.searchTerm || this.selectedVillage);

    const value: LocationAddress = {
      stateProvinceRegion: provinceLabel,
      countyDistrict: districtLabel,
      subdistrictMunicipality: subdistrictLabel,
      communeVillage: villageLabel,
      line: this.addressLine,
      zip: this.zipCode,
      code: this.selectedVillage || this.selectedSubdistrict || this.selectedDistrict || this.selectedProvince,
      longitude: null,
      latitude: null,
    };
    this.valueChange.emit(value);
    this.addressChange.emit(value);
  }

  /**
   * Resolves a location option code to its display label.
   *
   * @param options - The list of location options to search
   * @param code - The location code to resolve
   * @returns The display label if found, otherwise returns the code itself
   */
  private getOptionLabel(options: LocationOption[], code: string): string {
    const option = options.find((o) => o.value === code);
    return option?.label || code;
  }
}
